import {CommonModule} from '@angular/common';
import {ChangeDetectorRef, Component, Input, OnDestroy} from '@angular/core';
import {firstValueFrom, Observable, Subscription, take} from 'rxjs';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {CurrentUserViewModel} from '../../../core/models/current-user.view-model';
import {MediaTypeEnum} from '../../../core/enums/media-type.enum';
import {ModalStateEvent} from '../../modal/modal-state-event';
import {ModalService} from '../../modal/modal.service';
import {OverlayGalleryModalResult} from '../../modal/components/overlay-gallery/overlay-gallery-modal.interface';
import {BottomSheetComponent} from '../../components/bottom-sheet/bottom-sheet.component';
import {MediaViewComponent} from '../../media-view/media-view.component';
import {MediaViewModule} from '../../media-view/media-view.module';
import {VideoService} from '../video.service';
import {PostViewModel} from '../post.view-model';
import {TimelineDataProviderService} from '../timeline.data-provider.service';

@Component({
  selector: 'app-post-media-grid',
  standalone: true,
  imports: [
    CommonModule,
    MediaViewModule,
  ],
  templateUrl: './post-media-grid.component.html',
  styleUrl: './post-media-grid.component.scss'
})
export class PostMediaGridComponent implements OnDestroy {
  @Input() post: PostViewModel | null = null;
  @Input() currentUser: CurrentUserViewModel | null = null;
  @Input() fetchDeleted = false;
  @Input() interactive = true;
  @Input() priority = false;
  /** Renders posters/thumbs only; used by non-interactive Home swipe previews. */
  @Input() thumbnailOnly = false;
  @Input() deferLoading = false;
  @Input() galleryPosts: PostViewModel[] = [];
  /** Optional live gallery context supplied by a paginated app-timeline. */
  @Input() galleryPostSequence$?: Observable<PostViewModel[]>;
  @Input() galleryLoadNextPostPage?: () => Promise<boolean>;
  @Input() galleryHasNextPostPage?: () => boolean;
  @Input() galleryPostPageSize?: number;
  @Input() galleryPrefetchAtPostIndex?: number;

  public activeIndex = 0;
  private gallerySubscription: Subscription | null = null;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly ms: ModalService,
    private readonly timelineDPS: TimelineDataProviderService,
    private readonly vs: VideoService,
  ) {
  }

  public getDisplayMediaCount(): number {
    return this.post?.media?.length || 0;
  }

  public getDisplayMediaAt(index: number): MediaObjectApiModel | null {
    return this.post?.media?.[index] || null;
  }

  public getDisplayMediaSlice(start: number, end?: number): MediaObjectApiModel[] {
    return (this.post?.media || []).slice(start, end);
  }

  public hasDisplayVideoMedia(): boolean {
    return !!this.post?.media?.some((media) => media?.type === MediaTypeEnum.VIDEO);
  }

  public imageContainerStyles(mediaObjectAM: MediaObjectApiModel | null, count: number): object {
    return {
      backgroundColor: 'black',
      backgroundSize: count > 1 ? 'cover' : 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      maxHeight: count > 1 && mediaObjectAM?.type !== MediaTypeEnum.VIDEO ? false : '960px'
    };
  }

  public getHiddenMediaCount(mediaCount: number): number {
    return Math.max(0, mediaCount - 5);
  }

  public openDisplayMedia(event: Event, index: number, object?: MediaViewComponent): void {
    if (!this.interactive) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (index >= (this.post?.media?.length || 0)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    void this.openGalleria(event, index, object);
  }

  private async openGalleria(event: Event, activeIndex: number, object?: MediaViewComponent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    if (BottomSheetComponent.hasAnyPresented()) {
      return;
    }

    if (!this.hasRenderableMedia(this.post)) {
      await this.refreshPostMedia();
      if (!this.hasRenderableMedia(this.post)) {
        return;
      }
    }

    this.activeIndex = activeIndex;
    const hasControllableSourcePlayer = !!object?.hasControllablePlayer();
    const isSourcePlaybackPaused = hasControllableSourcePlayer ? object.isPlaybackPaused() : true;
    if (hasControllableSourcePlayer && isSourcePlaybackPaused) {
      this.vs.pauseAllExcept(object.id);
      object.togglePlay({event});
      return;
    }

    this.vs.setAutoplaySuppressed(true);
    this.gallerySubscription?.unsubscribe();
    const galleryModal = this.ms.showOverlayGalleryModal({
      post: this.post,
      posts: this.getGalleryPosts(),
      postIndex: this.getGalleryPostIndex(),
      currentUser: this.currentUser,
      component: object,
      activeIndex,
      initialCurrentTime: object?.getPlaybackCurrentTime() ?? 0,
      initialWasPlaying: hasControllableSourcePlayer && !isSourcePlaybackPaused,
      sourceVideoKey: hasControllableSourcePlayer ? object?.videoKey ?? null : null,
      postSequence$: this.galleryPostSequence$,
      loadNextPostPage: this.galleryLoadNextPostPage,
      hasNextPostPage: this.galleryHasNextPostPage,
      postPageSize: this.galleryPostPageSize,
      prefetchAtPostIndex: this.galleryPrefetchAtPostIndex,
    });
    this.gallerySubscription = galleryModal.successState.pipe(take(1)).subscribe((modalStateEvent: ModalStateEvent) => {
      const result = modalStateEvent.data as OverlayGalleryModalResult;
      if (result.component?.hasControllablePlayer() && result.sourceVideoKey) {
        result.component.resumePlaybackFrom(result.currentTime, this.vs.muted);
      }
      setTimeout(() => this.vs.setAutoplaySuppressed(false), 200);
    });
  }

  ngOnDestroy(): void {
    this.gallerySubscription?.unsubscribe();
    this.gallerySubscription = null;
    this.vs.setAutoplaySuppressed(false);
    // The gallery context contains callbacks closed over TimelineComponent.
    // Clear it after the result subscription is gone so a delayed dynamic
    // view cannot retain an off-tab virtual timeline.
    this.galleryPostSequence$ = undefined;
    this.galleryLoadNextPostPage = undefined;
    this.galleryHasNextPostPage = undefined;
    this.galleryPostPageSize = undefined;
    this.galleryPrefetchAtPostIndex = undefined;
    this.galleryPosts = [];
    this.post = null;
    this.currentUser = null;
  }

  private getGalleryPosts(): PostViewModel[] {
    const posts = (this.galleryPosts || [])
      .filter((post) => !post?.isPlaceholder && !!post?.id && !!post?.media?.length)
      .map((post) => post.id === this.post?.id ? this.post : post);
    return posts.length ? posts : [this.post];
  }

  private getGalleryPostIndex(): number {
    const index = this.getGalleryPosts().findIndex((post) => post.id === this.post?.id);
    return Math.max(0, index);
  }

  private async refreshPostMedia(): Promise<void> {
    if (!this.post?.id) {
      return;
    }

    try {
      const latest = await firstValueFrom(this.timelineDPS.getPost(String(this.post.id), this.fetchDeleted));
      if (!latest) {
        return;
      }
      this.post = latest;
      this.cdr.detectChanges();
    } catch {
      // noop
    }
  }

  private hasRenderableMedia(post: PostViewModel | null | undefined): boolean {
    if (!post?.media?.length) {
      return false;
    }

    return post.media.some((media: any) => {
      if (media?.type === MediaTypeEnum.VIDEO) {
        return this.hasRenderableVideoSource(media);
      }

      const links = media?.hyperlinks || [];
      if (!Array.isArray(links) || !links.length) {
        return false;
      }

      const names = new Set(links.map((link: any) => link?.name).filter(Boolean));
      return names.has('thumb_md') || names.has('thumb_sm') || names.has('original');
    });
  }

  private hasRenderableVideoSource(media: any): boolean {
    const links = Array.isArray(media?.hyperlinks) ? media.hyperlinks : [];
    return links.some((link: any) => (
      (link?.name === 'm3u8' || link?.name === 'original') && !!link?.uri
    ));
  }
}
